<template>
  <v2-modal
    :show="show"
    size="lg"
    @close="closeModal"
  >
    <template #header>
      <div>
        <span class="v2-text-base v2-font-semibold">Teammates of </span>
        <span class="v2-teammates-modal__retailer-name">{{ retailerName }}</span>
      </div>
    </template>

    <div
      v-if="loading"
      class="v2-flex v2-justify-center v2-p-8"
    >
      <v2-spinner size="md" />
    </div>

    <div v-else>
      <div
        v-if="teammates.length === 0"
        class="v2-text-center v2-p-8 v2-text-muted"
      >
        No teammates found
      </div>

      <div
        v-else
        class="v2-teammates-modal__list"
      >
        <v2-table
          :columns="tableFields"
          :items="teammates"
        >
          <template #cell(name)="{ item }">
            {{ [item.firstName, item.lastName].filter(Boolean).join(' ') || '-' }}
          </template>
          <template #cell(email)="{ item }">
            {{ item.email }}
          </template>
          <template #cell(role)="{ item }">
            <v2-badge variant="primary">
              {{ item.role || 'N/A' }}
            </v2-badge>
          </template>
        </v2-table>
      </div>
    </div>

    <template #footer>
      <v2-button @click="openInviteModal">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="v2-mr-1"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle
            cx="8.5"
            cy="7"
            r="4"
          />
          <line
            x1="20"
            y1="8"
            x2="20"
            y2="14"
          />
          <line
            x1="23"
            y1="11"
            x2="17"
            y2="11"
          />
        </svg>
        Invite Teammate
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { GET_RETAILER_TEAMMATES } from '@/store/modules/connections.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'

const { RETAILER_TEAMMATES } = constants.TRACKS.ACTIONS

export default {
  name: 'V2RetailerTeammatesModal',
  components: {
    V2Modal,
    V2Spinner,
    V2Table,
    V2Badge,
    V2Button,
  },
  props: {
    show: { type: Boolean, default: false },
    retailerId: { type: String, default: null },
    retailerName: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      teammates: [],
      tableFields: [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'role', label: 'Role' },
      ],
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.onShown()
      } else {
        this.onHidden()
      }
    },
  },
  methods: {
    async fetchTeammates() {
      if (!this.retailerId) return
      this.loading = true
      try {
        const res = await this.$store.dispatch(GET_RETAILER_TEAMMATES, {
          retailerId: this.retailerId,
        })
        this.teammates = res?.data?.data || []
        analytics.track(RETAILER_TEAMMATES.BRAND_FETCH_TEAMMATES_SUCCESS, {
          retailerId: this.retailerId,
          teammatesCount: this.teammates.length,
        })
      } catch (err) {
        this.teammates = []
        analytics.track(RETAILER_TEAMMATES.BRAND_FETCH_TEAMMATES_FAILED, {
          retailerId: this.retailerId,
        })
      } finally {
        this.loading = false
      }
    },
    onShown() {
      analytics.track(RETAILER_TEAMMATES.BRAND_VIEWS_TEAMMATES_MODAL, {
        retailerId: this.retailerId,
        retailerName: this.retailerName,
      })
      this.fetchTeammates()
    },
    closeModal() {
      this.$emit('close')
    },
    onHidden() {
      analytics.track(RETAILER_TEAMMATES.BRAND_CLOSES_TEAMMATES_MODAL, {
        retailerId: this.retailerId,
      })
      this.teammates = []
    },
    openInviteModal() {
      analytics.track(RETAILER_TEAMMATES.BRAND_CLICKS_INVITE_TEAMMATE, {
        retailerId: this.retailerId,
      })
      this.$emit('open-invite')
    },
    refreshTeammates() {
      this.fetchTeammates()
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-teammates-modal {
  &__retailer-name {
    color: var(--v2-primary);
    font-weight: 700;
  }

  &__list {
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>
